
import React, { useCallback, useMemo, useEffect } from 'react'
import { type NodeProps } from '@xyflow/react'
import { TextFile } from '@/components/canvas/nodes'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'

/**
 * TextFileNode - React Flow custom node
 *
 * Replaces TextFileAdapter for the React Flow migration.
 * Wraps the existing TextFile variant component with React Flow handles.
 */

export interface TextFileNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function TextFileNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as TextFileNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  // Stable onChange callback
  const handleChange = useCallback((text: string) => {
    onStateChange((prevState: Record<string, unknown>) => ({ ...prevState, prompt: text }))
  }, [onStateChange])

  const handleSelect = useCallback(() => {
    // Selection handled by React Flow
  }, [])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts

  useEffect(() => {
    if (!action) return

    switch (action.type) {
      case 'clear':
        onStateChange(prev => ({ ...prev, prompt: '' }))
        break
      case 'update':
        if (action.params?.prompt !== undefined) {
          onStateChange(prev => ({ ...prev, prompt: action.params.prompt }))
        }
        break
    }

    onStateChange(prev => ({ ...prev, _action: null }))
  }, [actionTimestamp, action, onStateChange])

  const fileProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      initialText: genieState.prompt,
      width: NODE_DIMENSIONS.TextFile.width,
      height: NODE_DIMENSIONS.TextFile.height,
      onChange: handleChange,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
    }),
    [id, genieState, selected, handleChange, handleSelect, onHoverChange, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.TextFile

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <TextFile {...fileProps} />
    </>
  )
}

export const TextFileNode = React.memo(
  TextFileNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as TextFileNodeData
    const nextData = nextProps.data as unknown as TextFileNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.prompt === nextData.state.prompt &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.generationStatus === nextData.state.generationStatus &&
      prevData.state.generationError === nextData.state.generationError &&
      prevData.state.generationProgress === nextData.state.generationProgress &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

TextFileNode.displayName = 'TextFileNode'
